import { type SetStateAction, useCallback } from 'react';
import { useStorage } from './useStorage';

const callbacks = new Set<(key: string) => void>();

// fallback storage for data that cannot be stored in sessionStorage
export const fallbackStorage = new Map<string, unknown>();

/**
 * A React hook that provides state synchronization with the browser's sessionStorage.
 *
 * @template T - The type of the value stored in sessionStorage.
 *
 * @param {string} key - The key under which the value is stored in sessionStorage.
 * @param {T | undefined} initialValue - The initial value of the state. If the key is not found in sessionStorage, this value will be used.
 *
 * @returns {[T | undefined, (value: SetStateAction<T>) => void]} An array containing the current value and a function to update the value.
 */
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const resolveValue = useCallback(
    (newValue: SetStateAction<T>, currentValue: T): T => {
      const previousValue =
        currentValue || window.sessionStorage.getItem(key) || {};

      return typeof newValue === 'function'
        ? (newValue as (prevState: T | undefined) => T)(currentValue)
        : Array.isArray(newValue)
          ? newValue
          : previousValue
            ? { ...previousValue, ...newValue }
            : newValue;
    },
    [key],
  );

  return useStorage({
    callbacks,
    fallbackStorage,
    initialValue,
    key,
    resolveValue,
    storage: window.sessionStorage,
  });
}

export default useSessionStorage;
